import React from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  MapPinIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

const Leadership: React.FC = () => {
  const stats = [
    { value: "Shareholder", label: "Ownership", color: "text-primary-600", bg: "bg-primary-50" },
    { value: "Operations", label: "Advisory", color: "text-blue-600", bg: "bg-blue-50" },
    { value: "Tech", label: "Advisory", color: "text-indigo-600", bg: "bg-indigo-50" },
    { value: "Ongoing", label: "Engagement", color: "text-emerald-600", bg: "bg-emerald-50" }
  ];

  const responsibilities = [
    {
      title: "Shareholder",
      text: "Involved as an owner in the company's strategic direction, priorities and key business decisions.",
      dot: "bg-primary-600"
    },
    {
      title: "Operations Advisory",
      text: "Advising on processes, cost control and the chain from purchase order to delivery and invoice, drawing on years of running complex, regulated projects with many stakeholders.",
      dot: "bg-blue-600"
    },
    {
      title: "Tech Advisory",
      text: "Advising on digitalisation: using data to find where operations lose time and money, and where automation and AI can take manual work out of the process reliably.",
      dot: "bg-indigo-600"
    }
  ];

  return (
    <section id="leadership" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Ownership & Advisory
          </h2>
          <div className="w-24 h-1 bg-primary-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Bringing product, business and technology experience into a business I co-own
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-gray-900 via-blue-900 to-blue-900 p-8">
              <div className="flex items-center space-x-4 text-white">
                <a
                  href="https://www.petrotreatment.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl border border-white/30 transition-all duration-300 hover:scale-105 flex-shrink-0"
                >
                  <img
                    src="/companies/petrotreatment.png"
                    alt="Petrotreatment Logo"
                    className="h-10 w-10 object-contain brightness-0 invert"
                  />
                </a>
                <div>
                  <h3 className="text-2xl font-bold mb-1 text-white">Shareholder · Business & Tech Advisory</h3>
                  <a
                    href="https://www.petrotreatment.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-white font-medium transition-colors duration-300"
                  >
                    Petrotreatment Group
                  </a>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Header Info */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8 pb-6 border-b border-gray-200">
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">Egypt</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">Ongoing</span>
                </div>
                <div className="flex items-center">
                  <BriefcaseIcon className="h-4 w-4 mr-2 text-primary-600" />
                  <span className="font-medium">Shareholder</span>
                </div>
                <div className="flex items-center">
                  <svg className="h-4 w-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3" />
                  </svg>
                  <a
                    href="https://www.petrotreatment.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary-600 hover:text-primary-700 transition-colors duration-300"
                  >
                    www.petrotreatment.com
                  </a>
                </div>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {stats.map((stat) => (
                  <div key={stat.value} className={`text-center p-4 ${stat.bg} rounded-xl`}>
                    <div className={`text-xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Role */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6">My Role</h4>
                <div className="space-y-4">
                  {responsibilities.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: (index + 1) * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg"
                    >
                      <div className={`w-2 h-2 rounded-full ${item.dot} mt-3 flex-shrink-0`}></div>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>{item.title}:</strong> {item.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Focus Areas */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Focus Areas</h4>
                <div className="flex flex-wrap gap-3">
                  {["Strategy", "Operations", "Process Improvement", "Cost Control", "Digitalisation", "Automation", "Data"].map((tech, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-100 text-blue-800 text-sm font-medium rounded-full border border-blue-200 hover:shadow-md transition-shadow"
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Leadership;
